import type { IPencilOfferInputs, TermOverrides } from '@makemydeal/dr-platform-types';
import { logImplausibleMoneyFactor, withoutImplausibleRates } from './rateUtils';

type OverridesByTerm = Record<string | number, TermOverrides>;
export type SavedInputs = { byOfferType?: Partial<Record<string, IPencilOfferInputs>> };

const logDroppedSavedRates = (override: TermOverrides): void =>
    logImplausibleMoneyFactor('savedRateCleanUp', {
        outcome: 'dropped',
        rateType: override.rateType,
        sellRate: override.sellRate,
        buyRate: override.buyRate
    });

const cleanTermOverride = (override: TermOverrides): TermOverrides => {
    if (!override?.rateType) return override;

    const cleaned = withoutImplausibleRates(override, override.rateType);
    if (cleaned !== override) logDroppedSavedRates(override);
    return cleaned;
};

const cleanOverridesByTerm = (overrides: OverridesByTerm): OverridesByTerm => {
    const cleaned = Object.entries(overrides).map(([term, override]) => [term, cleanTermOverride(override)]);
    const changed = cleaned.some(([term, override]) => override !== overrides[term as string]);
    return changed ? Object.fromEntries(cleaned) : overrides;
};

const cleanOfferInputs = (inputs: IPencilOfferInputs | undefined): IPencilOfferInputs | undefined => {
    if (!inputs?.overridesByTerm) return inputs;

    const overridesByTerm = cleanOverridesByTerm(inputs.overridesByTerm);
    return overridesByTerm === inputs.overridesByTerm ? inputs : { ...inputs, overridesByTerm };
};

export const withoutImplausibleSavedTermRates = <T extends SavedInputs>(penciledInputs: T): T => {
    const byOfferType = penciledInputs?.byOfferType;
    if (!byOfferType) return penciledInputs;

    const cleaned = Object.entries(byOfferType).map(([offerKey, inputs]) => [offerKey, cleanOfferInputs(inputs)]);
    const changed = cleaned.some(([offerKey, inputs]) => inputs !== byOfferType[offerKey as string]);
    return changed ? { ...penciledInputs, byOfferType: Object.fromEntries(cleaned) } : penciledInputs;
};

export const SAVED_RATE_CLEAN_UP_TOGGLE = 'DE766960_Enable_Implausible_Saved_Rate_Clean_Up';

export const cleanSavedRatesWhenEnabled = <T extends SavedInputs>(
    inputs: T,
    featureToggles?: Partial<Record<string, boolean>> | null
): T => (featureToggles?.[SAVED_RATE_CLEAN_UP_TOGGLE] ? withoutImplausibleSavedTermRates(inputs) : inputs);
